import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { flowRight } from 'lodash';
import {
  ELSWithModalService
} from '@els/els-component-modal-react';
import cx from 'classnames';
import PageHeader from '../../components/page-header/PageHeader.component';
import {
  getProduct,
  getProductConfig
} from '../../utilities/product.utilities';
import {
  useHistory,
  useLocation
} from 'react-router';
import {
  ELSURLHelper
} from '@els/els-ui-common-react';
import { RoutePath } from '../../components/app/app.constants';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import RemediationActivityProgress from '../../components/remediation-activity-progress/RemediationActivityProgress.component';
import {
  RecommendationAttemptDto,
  RecommendationAttemptStatusDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RemediationActivityTypeDto,
  RemediationActivityTypeSettings,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RemediationContentTypeConfigMap } from '../../models/remediation.models';
import RecommendationPostPlayerNext from '../../components/remediation-post-player-next/RemediationPostPlayerNext.component';
import RemediationPostPlayerMessage from './RemediationPostPlayerMessage.component';
import RemediationQuiz from '../../components/remediation-quiz/RemediationQuiz.component';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  getActiveAssignmentActivityTypeSettings,
  getAssessmentGoalFromRecommendation,
  getAssessmentScoreDetails,
  getAssessmentSubmissionByAttempt,
  getCategoryGroupNameForGoal,
  getContentTypePassNumber,
  getGoalBreadcrumbs,
  getGoalFromRecommendation,
  getNextRecommendation,
  getQuizTotals,
  getRecommendationAttemptById,
  getRecommendationFromAttempt,
  getRecommendationTitle,
  getTotalProgress,
  handleResumeOrStartNewAttempt,
  isTotalPLPComplete
} from '../../utilities/remediation.utilities';
import {
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import { SherpathAIChatToggle } from '../../components/sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import {
  buildSherpathAIPageContext,
  PageContextType
} from '../../utilities/sherpath-ai-context.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';
import { buildPostPlayerPageContext } from '../../utilities/sherpath-ai-page-context-builders.utilities';
import { CompleteBanner } from '../../components/complete-banner/CompleteBanner.component';
import { SimpleAccordion } from '../../components/simple-accordion/SimpleAccordion.component';
import { BackToPlanButton } from '../../components/back-to-plan-button/BackToPlanButton.component';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';

enum RecommendationPostPlayerStep {
  RETURN_STATUS = 'RETURN_STATUS',
  NEXT_RECOMMENDATION = 'NEXT_RECOMMENDATION',
  CONTINUE_QUIZZING = 'CONTINUE_QUIZZING',
}

type RecommendationPostPlayerState = {
  step: RecommendationPostPlayerStep;
}

const defaultState = {
  step: RecommendationPostPlayerStep.RETURN_STATUS
};

type RecommendationPostPlayerPropsOnly = Record<string, never>;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const RecommendationPostPlayerComponent = (): JSX.Element => {

  const [state, setState] = useState<RecommendationPostPlayerState>(defaultState);
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const { header } = getProductConfig(getProduct(baseState?.remediationAssignmentWithSources));
  const history = useHistory();
  const location = useLocation();
  const mergeState = (newState: Partial<RecommendationPostPlayerState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState
      };
    });
  };

  useEffect(() => {
    const attempt = getRecommendationAttemptById(
      appStateRef,
      ELSURLHelper.getParameterByName('recommendationAttemptId', location.search)
    );
    const recommendation = getRecommendationFromAttempt(baseState?.recommendations, attempt);
    if (!recommendation) {
      return;
    }
    const submission = getAssessmentSubmissionByAttempt(attempt, baseState?.attemptAssessmentSubmissions);
    const scoreToPass = getContentTypePassNumber(recommendation, baseState);

    const scoreDetails = getAssessmentScoreDetails({
      recommendation,
      submission,
      scoreToPass,
      baseState
    });
    if (!scoreDetails) {
      return;
    }
    const {
      score,
      passed,
      assignmentScore,
      assignmentNumericGrade
    } = scoreDetails;

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.ASSESSMENT_COMPLETED,
        pageData: {
          education: {
            moduleId: recommendation.assessmentGoalVtwId,
            moduleName: recommendation.assessmentGoalType,
            assignmentType: recommendation.contentType,
            assignmentId: recommendation.id.toString(),
            assignmentScore,
            assignmentNumericGrade
          },
          content: [{
            type: recommendation.contentType,
            id: recommendation.contentId,
          }]
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.CONTENT_COMPLETED,
        props: {
          recommendationId: recommendation.id,
          recommendationAttemptId: attempt.id,
          contentType: recommendation.contentType,
          score,
          passed,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.eolsAssessment?.id
        }
      }
    });

  }, [
    baseState?.recommendationAttempts,
    baseState?.recommendations,
    baseState?.attemptAssessmentSubmissions,
    baseState?.quizAttemptDetails,
    location.search
  ]);

  const getPageContext = (): string => {
    if (!appState.remediationBaseState?.remediationAssignmentWithSources) {
      return null;
    }

    const attempt = getRecommendationAttemptById(
      appStateRef,
      ELSURLHelper.getParameterByName('recommendationAttemptId', location.search)
    );

    if (!attempt) {
      return null;
    }

    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }

    // Build full context with post-player-specific data
    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: appState.remediationBaseState.remediationAssignmentWithSources,
      assessmentMetrics: appState.remediationBaseState?.assessmentMetrics,
      assessmentSources: appState.remediationBaseState?.raasAssessmentSourceList,
      hesiExamInfo: appState.remediationBaseState?.hesiExamInfo,
      totalProgressPercent: getTotalProgress(appState.remediationBaseState),
      pageType: PageContextType.POST_PLAYER,
      pageSpecificData: buildPostPlayerPageContext({
        baseState: appState.remediationBaseState,
        attempt
      })
    });
  };

  const handleReturnNavigation = () => {
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:closePostPlayer`,
        }
      }
    });
    history.push(RoutePath.REMEDIATION_STUDENT_HOME);
  };

  const getNewAttemptBtnText = (recommendation: RecommendationDto, attempt: RecommendationAttemptDto) => {
    if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
      return 'Continue quizzing';
    }
    if (
      [
        RecommendationAttemptStatusDto.COMPLETED_PASSED,
        RecommendationAttemptStatusDto.COMPLETED_FAILED
      ].includes(attempt.status)
    ) {
      return 'Start new attempt';
    }
    return `Resume ${RemediationContentTypeConfigMap[recommendation.contentType].titleSingular.toLowerCase()}`;
  };

  const showContinueBtn = (recommendation: RecommendationDto, attempt: RecommendationAttemptDto) => {
    if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
      return true
    }
    // Show button for failed attempts (so user can retry) and in-progress attempts
    // Hide button only for passed attempts
    return attempt.status !== RecommendationAttemptStatusDto.COMPLETED_PASSED
  };

  const getAttemptButtonTypeForAnalytics = (recommendation: RecommendationDto, attempt: RecommendationAttemptDto): string => {
    if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
      return `plp:${AdobeAnalyticsButtonType.BUTTON}:continueQuizzing`;
    }
    if (
      [
        RecommendationAttemptStatusDto.COMPLETED_PASSED,
        RecommendationAttemptStatusDto.COMPLETED_FAILED
      ].includes(attempt.status)
    ) {
      return `plp:${AdobeAnalyticsButtonType.BUTTON}:startNewAttempt`;
    }
    return `plp:${AdobeAnalyticsButtonType.BUTTON}:resumeAttempt`;
  };

  const attempt = getRecommendationAttemptById(
    appStateRef,
    ELSURLHelper.getParameterByName('recommendationAttemptId', location.search)
  );
  const recommendation = getRecommendationFromAttempt(baseState?.recommendations, attempt);

  if (!recommendation) {
    return null;
  }

  const activityType = recommendation.assessmentGoalType;
  const assessmentGoal = getAssessmentGoalFromRecommendation(baseState?.eolsAssessment, recommendation);
  const topicGoal = getGoalFromRecommendation(baseState.eolsAssessment?.assessmentGoals, recommendation);
  const isTotalComplete = isTotalPLPComplete(baseState);
  const nextRecommendation = getNextRecommendation(baseState, assessmentGoal);
  const quizStatusCounts = getQuizTotals(baseState);
  if (!recommendation) {
    return null;
  }
  const showNextRecBtn = !isTotalComplete && !!nextRecommendation;

  return (
    <div className="c-ssa-remediation-post-player">
      <div className="c-ssa-remediation-post-player__header">
        <PageHeader onCloseClick={handleReturnNavigation} title={header.title} titleClass={header.titleClass} logo={header.logo}>
          <SherpathAIChatToggle
            remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
            pageContextType={PageContextType.POST_PLAYER}
          />
        </PageHeader>
      </div>

      <div className="c-ssa-remediation-post-player__main">
        <div className={cx('c-ssa-remediation-post-player__body', {
          'c-ssa-remediation-post-player__body--chat-active': appState.isChatActive
        })}>

          {
            isTotalComplete && state.step === RecommendationPostPlayerStep.RETURN_STATUS && (
              <div className="c-ssa-remediation-post-player____complete_window">
                <div className="c-ssa-remediation-post-player____complete_window_progress">
                  <FlexLayout
                    modifiers={[
                      FlexLayoutModifier.GUTTERS_2X,
                      FlexLayoutModifier.WRAP_AT_MOBILE
                    ]}
                  >
                    {
                      getActiveAssignmentActivityTypeSettings(baseState)
                        .map((activityTypeSettings: RemediationActivityTypeSettings) => {
                          return (
                            <FlexItem key={activityTypeSettings.activityType}>
                              <RemediationActivityProgress
                                assessmentGoals={baseState.eolsAssessment?.assessmentGoals}
                                activityType={activityTypeSettings.activityType}
                              />
                            </FlexItem>
                          );
                        })
                    }
                  </FlexLayout>
                </div>
              </div>
            )
          }

          {
            appState.pendingRequestCount <= 0 && (
              <div className="c-ssa-remediation-post-player__window">

                {
                  state.step === RecommendationPostPlayerStep.RETURN_STATUS && (

                    <div>

                      {
                        isTotalComplete && <CompleteBanner />
                      }

                      {
                        !isTotalComplete && (
                          <div className="c-ssa-remediation-post-player__progress-bar">
                            <RemediationActivityProgress
                              assessmentGoals={baseState.eolsAssessment?.assessmentGoals}
                              activityType={activityType}
                            />
                          </div>
                        )
                      }

                      {
                        recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ && (
                          <div>
                            <div className="o-els-container">

                              <SimpleAccordion
                                title="Activity details"
                                onToggle={(isExpanded) => {
                                  trackAdobeAction({
                                    appStateRef,
                                    actionProps: {
                                      action: AdobeAnalyticsAction.BUTTON_CLICK,
                                      pageData: {
                                        buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:${isExpanded ? 'expandActivityDetails' : 'collapseActivityDetails'}`,
                                      }
                                    }
                                  });
                                  trackNewRelicAction({
                                    appStateRef,
                                    actionProps: {
                                      action: NewRelicAnalyticsAction.ACTIVITY_DETAILS_TOGGLED,
                                      props: {
                                        targetState: isExpanded ? 'expanded' : 'collapsed',
                                        recommendationId: recommendation?.id,
                                        contentType: recommendation?.contentType,
                                        remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
                                        assessmentId: baseState.eolsAssessment?.id
                                      }
                                    }
                                  });
                                }}
                              >
                                <div className="">
                                  <div className="o-els-container">{getRecommendationTitle(recommendation)}</div>
                                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.LEFT]}>
                                    <FlexItem>
                                      <div className="u-els-color-n6">Type:</div>
                                      <div>{RemediationContentTypeConfigMap[recommendation.contentType].displayName}</div>
                                    </FlexItem>
                                    <FlexItem isRender={recommendation.assessmentGoalType === RemediationActivityTypeDto.REVIEW_BY_TAXON}>
                                      <div className="u-els-color-n6">Topic:</div>
                                      <div>
                                        {getGoalBreadcrumbs(topicGoal, getCategoryGroupNameForGoal(topicGoal, baseState)).pathFullDisplay}
                                      </div>
                                    </FlexItem>
                                  </FlexLayout>
                                </div>
                              </SimpleAccordion>
                            </div>
                            <div className="c-els-divider" />
                          </div>
                        )
                      }


                      <div className="o-els-container o-els-container--2x">
                        <RemediationPostPlayerMessage
                          recommendation={recommendation}
                          attempt={attempt}
                          appLinkData={appState.appLinkData}
                        />
                      </div>
                    </div>
                  )
                }

                {
                  state.step === RecommendationPostPlayerStep.CONTINUE_QUIZZING && (
                    <div className="o-els-container">
                      <RemediationQuiz
                        assessmentGoal={assessmentGoal}
                        recommendation={recommendation}
                        metricCounts={quizStatusCounts}
                        showReviewQuizButton={false}
                        continueQuizzing
                        showBorder={false}
                      />
                    </div>
                  )
                }

                {
                  state.step === RecommendationPostPlayerStep.NEXT_RECOMMENDATION && (
                    <div>
                      <h3 className="u-els-text-center u-els-padding-bottom-1x"><strong>Next recommendation</strong></h3>
                      <div className="o-els-container">
                        <RecommendationPostPlayerNext
                          nextRecommendation={nextRecommendation}
                        />
                      </div>
                    </div>
                  )
                }

                <div className="u-els-margin-top-2x">
                  <FlexLayout modifiers={[
                    FlexLayoutModifier.GUTTERS,
                    FlexLayoutModifier.RIGHT,
                    FlexLayoutModifier.MIDDLE
                  ]}>

                    <FlexItem>
                      <BackToPlanButton
                        appStateRef={appStateRef}
                        recommendationId={recommendation?.id}
                        contentType={recommendation?.contentType}
                      />
                    </FlexItem>

                    <FlexItem isRender={
                      state.step === RecommendationPostPlayerStep.NEXT_RECOMMENDATION
                      || state.step === RecommendationPostPlayerStep.CONTINUE_QUIZZING
                    }>
                      <button
                        type="button"
                        className="c-els-button c-els-button--secondary c-els-button--small"
                        onClick={() => {
                          trackAdobeAction({
                            appStateRef,
                            actionProps: {
                              action: AdobeAnalyticsAction.BUTTON_CLICK,
                              pageData: {
                                buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:backFromNextRecommendation`,
                              }
                            }
                          });
                          mergeState({ step: RecommendationPostPlayerStep.RETURN_STATUS });
                        }}
                      >
                        Back
                      </button>
                    </FlexItem>

                    <FlexItem
                      isRender={
                        state.step === RecommendationPostPlayerStep.RETURN_STATUS
                        && showContinueBtn(recommendation, attempt)
                      }
                    >
                      <button
                        type="button"
                        className="c-els-button c-els-button--secondary c-els-button--small"
                        onClick={() => {
                          trackAdobeAction({
                            appStateRef,
                            actionProps: {
                              action: AdobeAnalyticsAction.BUTTON_CLICK,
                              pageData: {
                                buttonType: getAttemptButtonTypeForAnalytics(recommendation, attempt),
                              }
                            }
                          });
                          if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
                            mergeState({ step: RecommendationPostPlayerStep.CONTINUE_QUIZZING });
                            return;
                          }
                          setAppState((prev) => ({ ...prev, isLaunchingRecommendation: true }));
                          handleResumeOrStartNewAttempt({
                            setAppState,
                            appState,
                            appStateRef,
                            recommendation,
                            recommendationAttempt: attempt,
                            baseState,
                            history,
                            ref: RoutePath.REMEDIATION_STUDENT_HOME
                          }).catch(() => setAppState((prev) => ({ ...prev, isLaunchingRecommendation: false })));
                        }}
                      >
                        {
                          getNewAttemptBtnText(recommendation, attempt)
                        }
                      </button>
                    </FlexItem>

                    <FlexItem
                      isRender={
                        state.step === RecommendationPostPlayerStep.RETURN_STATUS
                        && showNextRecBtn
                      }
                    >
                      <button
                        type="button"
                        className="c-els-button c-els-button--primary c-els-button--small"
                        onClick={() => {
                          trackAdobeAction({
                            appStateRef,
                            actionProps: {
                              action: AdobeAnalyticsAction.BUTTON_CLICK,
                              pageData: {
                                buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:nextRecommendation`,
                              }
                            }
                          });
                          trackNewRelicAction({
                            appStateRef,
                            actionProps: {
                              action: NewRelicAnalyticsAction.NEXT_RECOMMENDATION_BUTTON_CLICKED,
                              props: {
                                recommendationId: recommendation?.id,
                                contentType: recommendation?.contentType,
                                remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
                                assessmentId: baseState.eolsAssessment?.id
                              }
                            }
                          });
                          mergeState({ step: RecommendationPostPlayerStep.NEXT_RECOMMENDATION });
                        }}
                      >
                        See next recommendation
                      </button>
                    </FlexItem>

                  </FlexLayout>
                </div>

              </div>
            )
          }

        </div>

        <SherpathAIChatSidebar
          remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
          pageContext={getPageContext()}
          pageContextType={PageContextType.POST_PLAYER}
        />
      </div>

    </div>
  );
};

const enhance = flowRight(
  ELSWithModalService,
  withPageLoader,
);

const RecommendationPostPlayer = enhance(RecommendationPostPlayerComponent) as React.FC<RecommendationPostPlayerPropsOnly>;

export default RecommendationPostPlayer;
